import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api } from '@/lib/utils';

interface AuthUser {
  user: {
    userId: string;
    workspaceId: string;
    email: string;
    name: string;
    emailVerified: boolean;
    referralCode?: string | null;
    isStaff?: boolean;
    businessId?: string;
  };
  isStaff?: boolean;
  workspace?: { id: string; name: string; timezone: string; trialEndsAt?: string };
  settings?: { onboardingComplete: boolean; demoSeedBatchId?: string | null; demoSeedAt?: string | null };
  usage?: {
    tier?: string;
    tierName?: string;
    subscribedTier?: string | null;
    subscribedTierName?: string | null;
    onTrial?: boolean;
    hasActiveSubscription?: boolean;
    subscriptionStatus?: 'none' | 'active' | 'trialing' | 'past_due' | 'canceled' | 'paused';
    subscriptionCancelAt?: string | null;
    trialDaysLeft: number | null;
    trialEndsAt?: string | null;
    agreements: { used: number; limit: number; percent: number };
    customers?: { used: number; limit: number; percent: number };
  };
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  refresh: () => Promise<AuthUser | null>;
  logout: () => Promise<void>;
  usage: AuthUser['usage'];
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async (): Promise<AuthUser | null> => {
    try {
      const data = await api<AuthUser>('/api/v1/auth/me');
      setUser(data);
      return data;
    } catch {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await api('/api/v1/auth/logout', { method: 'POST' });
    setUser(null);
    window.location.href = '/login';
  };

  useEffect(() => {
    refresh();
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, refresh, logout, usage: user?.usage }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
